import * as Api from '@ViewModels';
import { IImageLibraryItem, ImageLibrarySource } from './types';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateImageLibraryKeywordsQuery } from './useImageLibraryKeywordsQuery';
import { invalidateImageLibraryQuery } from './useImageLibraryQuery';

interface MutationCallbacks {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (result: IImageLibraryItem) => void;
}

/**
 * Adds an image the account already owns. Uploads and stock picks join the library at creation time via
 * the `addToLibrary` flag instead; this covers AI-generated images, which exist as attachments for every
 * generation attempt and should only be kept once the user picks one.
 */
export function useAddToImageLibraryMutation({ onError, onSuccess }: MutationCallbacks = {}) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({
			fileAttachmentId,
			source,
			impersonationContext,
		}: {
			fileAttachmentId: string;
			source: ImageLibrarySource;
			impersonationContext?: Api.IImpersonationContext;
		}) => {
			return userSession.webServiceHelper.callAsync<IImageLibraryItem>(
				Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: 'imageLibrary' }),
				'POST',
				{ fileAttachmentId, source }
			);
		},
		onError,
		onSuccess: result => {
			invalidateImageLibraryQuery();
			onSuccess?.(result);
		},
	});
}

export function useRenameImageLibraryEntryMutation({ onError, onSuccess }: MutationCallbacks = {}) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({
			itemId,
			displayName,
			impersonationContext,
		}: {
			itemId: string;
			displayName: string;
			impersonationContext?: Api.IImpersonationContext;
		}) => {
			return userSession.webServiceHelper.callAsync<IImageLibraryItem>(
				Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: `imageLibrary/${itemId}` }),
				'PATCH',
				{ displayName }
			);
		},
		onError,
		onSuccess: result => {
			invalidateImageLibraryQuery();
			onSuccess?.(result);
		},
	});
}

/**
 * Replaces the folders an entry is filed under. An empty array files it back under Unfiled.
 */
export function useUpdateImageLibraryKeywordsMutation({ onError, onSuccess }: MutationCallbacks = {}) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({
			itemId,
			keywords,
			impersonationContext,
		}: {
			itemId: string;
			keywords: string[];
			impersonationContext?: Api.IImpersonationContext;
		}) => {
			return userSession.webServiceHelper.callAsync<IImageLibraryItem>(
				Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: `imageLibrary/${itemId}/keywords` }),
				'PUT',
				keywords
			);
		},
		onError,
		onSuccess: result => {
			invalidateImageLibraryQuery();
			// A first or last use of a folder adds or removes it from the rail, so the counts move too.
			invalidateImageLibraryKeywordsQuery();
			onSuccess?.(result);
		},
	});
}

/**
 * Deletes the library entry only. The attachment survives, so emails, posts, and templates that already
 * reference the image keep rendering.
 */
export function useRemoveFromImageLibraryMutation({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: () => void;
} = {}) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({
			itemId,
			impersonationContext,
		}: {
			itemId: string;
			impersonationContext?: Api.IImpersonationContext;
		}) => {
			return userSession.webServiceHelper.callAsync(
				Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: `imageLibrary/${itemId}` }),
				'DELETE'
			);
		},
		onError,
		onSuccess: () => {
			invalidateImageLibraryQuery();
			invalidateImageLibraryKeywordsQuery();
			onSuccess?.();
		},
	});
}
